import {Injectable} from '@angular/core';
import {Platform} from '@ionic/angular';
import {State, StateInterface} from '../../interfaces/biozoom-api/app-configuration';
import {combineLatest, from, Observable, of, timer} from 'rxjs';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpProgressEvent,
  HttpResponse,
  HttpUploadProgressEvent
} from '@angular/common/http';
import {catchError, concatMap, delay, filter, repeat, switchMap, take, timeout} from 'rxjs/operators';
import {blockifyUpdate, evalJson, hexToBin, parseHandRecognition, readBlobAsText} from '../../methods/blockFormat';
import {ApiProgressUtilsService} from '../api/api-progress-utils.service';
import {PipelineStageConfig} from '../../interfaces/biozoom-api/pipeline';
import {NetworkService} from '../network.service';
import {SCANNER_APMODE_URL} from '../../methods/helpers';
import {ScannerSignals} from '../../interfaces/scanner-api/scanner';
import {retryBackoff} from 'backoff-rxjs';
import {HandrecognitionResult} from '../../interfaces/scanner-api/handrecognition-result';
import {ScannerStatus} from '../../interfaces/scanner-api/scanner-status';

const WIFI_STATUS_REQUEST_TIMEOUT = 2500;
const WIFI_READY_TIMEOUT = 15000;
const WIFI_READY_RETRY_DELAY = 500;

@Injectable({
  providedIn: 'root'
})
export class WifiApiService {

  constructor(
    private platform: Platform,
    private http: HttpClient,
    private networkService: NetworkService,
  ) {
  }

  private sleep(duration: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, duration));
  }

  private getStatusRequest(): Observable<ScannerStatus> {
    return this.http.get<ScannerStatus>(SCANNER_APMODE_URL + '/status').pipe(
      timeout(WIFI_STATUS_REQUEST_TIMEOUT),
    );
  }

  private isValidStatus(status: ScannerStatus): boolean {
    return !!status && Object.keys(status).length > 0;
  }

  private logWifiStatusFailure(phase: string, attempt: number, startedAt: number, error: any) {
    const elapsed = Date.now() - startedAt;
    console.log('Wi-Fi scanner status failed', {
      phase,
      attempt,
      elapsed,
      name: error?.name,
      status: error?.status,
      message: error?.message ?? error?.toString?.() ?? error,
    });
  }

  async waitForScannerReady(timeoutMs = WIFI_READY_TIMEOUT): Promise<ScannerStatus> {
    const startedAt = Date.now();
    let attempt = 0;
    let lastError: any = new Error('Scanner status did not become ready');

    while (Date.now() - startedAt < timeoutMs) {
      attempt += 1;

      try {
        const status = await this.getStatusRequest().toPromise();
        if (this.isValidStatus(status)) {
          return status;
        }
        lastError = new Error('Scanner returned empty status');
        this.logWifiStatusFailure('readiness', attempt, startedAt, lastError);
      } catch (error) {
        lastError = error;
        this.logWifiStatusFailure('readiness', attempt, startedAt, error);
      }

      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining > 0) {
        await this.sleep(Math.min(WIFI_READY_RETRY_DELAY, remaining));
      }
    }

    throw lastError;
  }

  private getDataAsText(
    key: string,
    demoModeDelay: number,
    maxMeasurementTime: number,
    state: StateInterface
  ): Observable<HttpEvent<any>> {
    let request: Observable<HttpEvent<any>>;

    request = this.http.get(SCANNER_APMODE_URL + `/${key}`,
      {
        responseType: 'text', reportProgress: true, observe: 'events'
      }
    );
    return request.pipe(
      timeout(maxMeasurementTime),
    );
  }


  doAOXMeasurementObservable(
    measurementType: string, state: StateInterface, gen1Type: string, v2: boolean, oldApi = false
  ): Observable<HttpEvent<any>> {
    if (!v2) {
      switch (gen1Type) {
        case 'aox': return this.getDataAsText('m', 10000, 20000, state);
        case 'hrv-test':
          return combineLatest([
            oldApi ?
              of<HttpResponse<any>>(new HttpResponse()) :
              this.getDataAsText('flashDeleteHRV', 2000, 15000, state),
            this.getDataAsText('hrvTest', 2000, 15000, state)
          ]).pipe(
            concatMap((result) => of<HttpEvent<any>>(result[1]))
          );
        case 'hrv': return this.getDataAsText('hrv', 10000, 100000, state);
        case 'hrvOfflineGet': return this.getHrvOfflineMeasurement(state);
        case 'hrvOfflineDo': return this.doHrvOfflineMeasurement(state);
        default: return;
      }
    }

    const baseUrl = SCANNER_APMODE_URL;
    let totalBlocks = 5773;
    let command = 'm?type=aox-prod';
    let index = 0;
    switch (measurementType) {
      case 'full': {
        totalBlocks = 5773;
        command = 'm?type=aox';
        break;
      }
      case 'front_half': {
        totalBlocks = 2887;
        command = 'm?type=aox-half';
        break;
      }
      case 'hrvOfflineDo':
      case 'hrv': {
        totalBlocks = 45000;
        command = 'm?type=hrv';
        break;
      }
      case 'hrv-test': {
        totalBlocks = 5000;
        command = 'm?type=hrv-test';
        break;
      }
      default: {
        totalBlocks = 5773;
        command = 'm?type=aox';
        break;
      }
    }
    const foundIndexes: boolean[] = Array(totalBlocks).fill(false);
    const resDict = {};
    let missingArray = [[0, 150]];
    let goodBlocks = 0;
    let startTS: number;
    const initFunc = async () => {
      if (index > 0) {
        return;
      }

      await fetch(`${baseUrl}/d`, {
        method: 'GET',
      });
      const resp = await fetch(`${baseUrl}/${command}`, {
        method: 'GET',
      });
      const json = await resp.json();
      index = json.measurement_nr;
      await new Promise((r => setTimeout(r, 250)));
      startTS = Date.now();
      return index;
    };

    return from(initFunc()).pipe(
      delay(100),
      switchMap(async resultIndex => {
        if (goodBlocks === totalBlocks) {
          return new HttpResponse({ body: missingArray.join('') });
        }

        const currentTS = Date.now();
        const blocksPerSecond = 400;
        const currentEntry = missingArray[0];
        const currentMax = Math.floor(((currentTS - startTS) / 1000) * blocksPerSecond);

        const start = currentEntry[0];
        let range = currentEntry[1];

        if (currentMax <= start) {
          return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
        }


        if (start + range > currentMax) {
          range = currentMax - start + 1;
        }
        range = Math.min(range, 600);

        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), 3000 + 10 * range);
        let resp: Response;
        try {
          resp = await fetch(`${baseUrl}/read-flash?nr=${resultIndex}&block=${start}&range=${range}`, {
            method: 'GET',
            signal: controller.signal
          });
        } catch (error) {
          return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
        } finally {
          clearTimeout(id);
        }
        if (resp.status >= 200 && resp.status < 300) {
          const json = await resp.json();
          const { status: statusEval, data: d, goodBlocks: gb, ok } =
            evalJson(json.data, resDict, totalBlocks, foundIndexes, command.includes('hrv'), false);
          if (ok) {
            goodBlocks = gb;
            missingArray = d;
          }
          if (statusEval) {
            return new HttpResponse({ body: missingArray.join('') });
          }
        }
        return { type: HttpEventType.DownloadProgress, loaded: (goodBlocks), total: totalBlocks } as HttpProgressEvent;
      }),
      repeat(),
    );
  }

  doHrvOfflineMeasurement(state: StateInterface): Observable<HttpEvent<any>> {
    const v2 = state.SCANNER_VERSION === 2;
    const command = v2 ? 'm?type=hrv' : 'hrvOffline';
    return this.getDataAsText(command, 500, 2000, state).pipe(
      filter((event) => event instanceof HttpResponse),
      concatMap(_ => {
        const total = state.USB_MODE ? 10000 : 95000;
        const period = Math.round(total / 100) + 1;

        return timer(0, period).pipe(
          take(102),
          concatMap(count => {
            const event: HttpUploadProgressEvent = {
              type: HttpEventType.UploadProgress,
              total,
              loaded: period * count
            };

            if (count === 0) {
              return from(this.disconnectScanner(state)).pipe(
                concatMap(() => of<HttpUploadProgressEvent>(event))
              );
            // } else if (count === 101 && full) {
            //   // TODO: replace function dummy
            //   return from(this.networkService.connectToScannerWifi(state)).pipe(
            //     switchMap((connected) => {
            //       console.log('connected: ' + connected);
            //       return this.getDataAsText('flashReadHRV', 5000, 20000, state).pipe(retryBackoff({
            //         initialInterval: 1000,
            //         maxRetries: 5,
            //       }));
            //     }));
            } else {
              if (event.loaded > event.total) {
                event.loaded = event.total - 1;
              }
              if (count === 100) {
                event.loaded = event.total;
              }
              return of<HttpUploadProgressEvent>(event);
            }
          })
        );
      })
    );
  }

  async doHandRecognitionObservable(state, handOff, wifiFirmware): Promise<boolean> {
    const infFirmware = state.SCANNER_STATUS?.sensor_firmware ?? 1;
    const major = Number(wifiFirmware?.split('.')[0] ?? '0');
    if (major < 10 || infFirmware < 604) {
      const signalLMS = [
        ScannerSignals.SCANNER_SIGNAL_LOGO_LARGE,
        ScannerSignals.SCANNER_SIGNAL_LOGO_MEDIUM,
        ScannerSignals.SCANNER_SIGNAL_LOGO_SMALL
      ];
      const signalLM = [
        ScannerSignals.SCANNER_SIGNAL_LOGO_MEDIUM,
        ScannerSignals.SCANNER_SIGNAL_LOGO_LARGE
      ];

      await this.setSignalOnOff(handOff ? signalLMS : [], handOff ? [] : signalLM, state).toPromise();
      const handrecognitionResult = await this.http.get<HandrecognitionResult>(SCANNER_APMODE_URL + '/h').toPromise();
      let bool;
      if (!handOff) {
        bool = handrecognitionResult.hand === 0;
      } else {
        bool = handrecognitionResult.hand !== 0;
      }
      if (bool) {
        await this.setSignalOnOff(handOff ? signalLMS : [], handOff ? [] : signalLM, state).toPromise();
        await this.setSignalOnOff([], signalLMS, state).toPromise();
      }
      return bool;
    }
    return this.doHandRecognitionObservable2(state, handOff);
  }

  async doHandRecognitionObservable2(state, handOff): Promise<boolean> {
    const start = handOff ? 134 : 110;
    const count = handOff ? 10 : 13;
    const cycles = handOff ? 25 : 5;

    const command = `h?start=${start}&count=${count}&cycles=${cycles}`;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 2000);
    let resp: Response;
    try {
      resp = await fetch(`${SCANNER_APMODE_URL}/${command}`, {
        method: 'GET',
        signal: controller.signal
      });
    } catch (error) {
      return false;
    } finally {
      clearTimeout(id);
    }
    if (resp.status >= 200 && resp.status < 300) {
      const respData = await resp.json();
      const result = parseHandRecognition(respData.data, handOff, start, count, cycles);
      if (result) {
        const dataToSend = [];
        for (let i = 0; i < respData.data.length; i++) {
          if (((i + 1) % (cycles + 2)) === 0) {
            continue;
          }
          dataToSend.push(respData.data[i]);
        }
        state.HAND_RECOGNITION_DATA = dataToSend;
      }
      return result;
    }
    return false;
  }

  async deleteFlash(pipelineConfig: PipelineStageConfig) {
    await this.networkService.connectToScannerWifi(pipelineConfig.state.persistentValues);
    const status = await this.getStatus();
    if (status && Object.keys(status).length > 0) {
      pipelineConfig.setState(State.SCANNER_STATUS, status);
    }
    const x = pipelineConfig.state.persistentValues.SCANNER_STATUS;
    const major = Number(x.wifi_firmware.split('.')[0]);
    const cmd = major < 10 ? '/flashDeleteUnhealthy' : '/erase-chip';
    // if (major >= 10) {
    //   return true;
    // }
    const p = this.http.get(SCANNER_APMODE_URL + cmd, {observe: 'response'}).toPromise();
    const r = await p;
    return major < 10 ? r.status !== 202 : r.status === 202;
  }

  setSignalOnOff(on: ScannerSignals[], off: ScannerSignals[], state: StateInterface) {
    let onSum = 0;
    on.forEach((value) => {
      onSum += value;
    });
    let offSum = 0;
    off.forEach((value) => {
      offSum += value;
    });

    return this.http.get(SCANNER_APMODE_URL + `/signal?on=${onSum}&off=${offSum}`).pipe(
      catchError(_ => of(''))
    );
  }

  getHrvOfflineMeasurement(state: StateInterface) {
    const v2 = state.SCANNER_VERSION === 2;
    const command = v2 ? 'read-flash?file=true' : 'flashReadHRV';
    return this.getDataAsText(command, 5000, 20000, state).pipe(retryBackoff({
      initialInterval: 1000,
      maxRetries: 1,
    }));
  }

  // updateScannerFirmwareOffline(filename, state: StateInterface) {
  //   return this.http.get(`/assets/firmware/${filename}`, { responseType: 'blob' }).pipe(
  //     concatMap((binFile) => {
  //       const fd = new FormData();
  //       fd.append('update', binFile, filename);
  //       return this.http.post(SCANNER_APMODE_URL + '/updateUpload', fd,
  //         { responseType: 'text' });
  //     })
  //   );
  // }

  getIdent() {
    return this.getStatusRequest().pipe(
      switchMap((e) => {
        return of(e.serial);
      }),
      retryBackoff({
        initialInterval: 1000,
        maxRetries: 3,
      }));
  }

  async getStatus() {
    return await this.getStatusRequest().toPromise();
  }


  updateInfineon(pipelineConfig: PipelineStageConfig, bootloader: boolean) {
    return of({}).pipe(
      switchMap(async () => {
        const [con, ser, _ver] =
          await this.networkService.connectToScannerWifi(pipelineConfig.state.persistentValues);
        if (pipelineConfig.state.persistentValues.DEMO_MODE || pipelineConfig.state.persistentValues.CONNECTION_TYPE !== 'WIFI') {
          return '';
        }
        const filename = bootloader ? 'bootloader.hex' : pipelineConfig.config.sensor_filename;
        const blobIn = await this.http.get(`/assets/firmware/${filename}`, {
          responseType: 'blob',
          // headers: { Accept: 'text/plain' }
        }).toPromise();

        const binFile = await readBlobAsText(blobIn);
        const {dataBin, startAddr} = hexToBin(binFile, bootloader ? 4096 : 0);
        const updateStr = blockifyUpdate(dataBin, false, false);
        const uint8Array = new Uint8Array(updateStr.length);
        for (let i = 0; i < updateStr.length; i++) {
          uint8Array[i] = updateStr.charCodeAt(i);
        }
        // console.log(uint8Array);
        const fd = new FormData();
        const blob = new Blob([uint8Array], { type: 'application/octet-stream' });
        fd.append('update', blob, 'inf.hex');
        // @ts-ignore
        const url = `${ScannerApiService.SCANNER_APMODE_URL}/${bootloader ? 'updateUploadInfineonBootloader' : 'updateUploadInfineon'}?length=${dataBin.length}&chunkSize=262&startAddr=${startAddr}`;
        await this.http.post(url, fd,
          { responseType: 'text' }
        ).toPromise();
        if (bootloader) {
          return '';
        }

        const url2 = `${SCANNER_APMODE_URL}/updateInitialize`;
        await this.http.get(url2,
          { responseType: 'text' }
        ).toPromise();
        return '';
      })
    );
  }


  async updateESP(pipelineConfig: PipelineStageConfig, downgrade: boolean = false) {
    let res = false;
    try {
      if (pipelineConfig?.state?.persistentValues?.SCANNER_SERIAL === 'W') {
        return false;
      }
      const x = pipelineConfig.state.persistentValues.SCANNER_STATUS;
      const versionParsed = x.wifi_firmware.split('.').map((p) => Number(p.padStart(2, '0')));
      const major = Number(x.wifi_firmware.split('.')[0]);
      let targetVersion: string;
      let targetVersionFilename: string;
      if (major === 2) {
        if (downgrade) {
          targetVersion = pipelineConfig.config.firmware_hw2_version_old;
          targetVersionFilename = pipelineConfig.config.firmware_hw2_filename_old;
        } else {
          targetVersion = pipelineConfig.config.firmware_hw2_version;
          targetVersionFilename = pipelineConfig.config.firmware_hw2_filename;
        }
      } else if (major === 3) {
        if (downgrade) {
          targetVersion = pipelineConfig.config.firmware_hw3_version_old;
          targetVersionFilename = pipelineConfig.config.firmware_hw3_filename_old;
        } else {
          targetVersion = pipelineConfig.config.firmware_hw3_version;
          targetVersionFilename = pipelineConfig.config.firmware_hw3_filename;
        }
      } else if (major === 4) {
        if (downgrade) {
          targetVersion = pipelineConfig.config.firmware_hw4_version_old;
          targetVersionFilename = pipelineConfig.config.firmware_hw4_filename_old;
        } else {
          targetVersion = pipelineConfig.config.firmware_hw4_version;
          targetVersionFilename = pipelineConfig.config.firmware_hw4_filename;
        }
      } else if (major >= 10 && major < 20) {
        targetVersion = pipelineConfig.config.firmware_10_version;
        targetVersionFilename = pipelineConfig.config.firmware_10_filename;
      } else if (major >= 20) {
        targetVersion = pipelineConfig.config.firmware_20_version;
        targetVersionFilename = pipelineConfig.config.firmware_20_filename;
      }
      if (!targetVersion) {
        return false;
      }
      const targetVersionParsed = targetVersion.split('.').map((p) => Number(p.padStart(2, '0')));

      let shouldUpdate = false;
      for (let i = 0; i < 3; i++) {
        if (versionParsed[i] === targetVersionParsed[i]) {
          continue;
        } else if (versionParsed[i] > targetVersionParsed[i]) {
          shouldUpdate = false;
          break;
        } else {
          shouldUpdate = true;
          break;
        }
      }
      if (pipelineConfig.state.persistentValues.SCANNER_VERSION === 1 && major >= 10) {
        shouldUpdate = true;
      }
      if (downgrade) {
        shouldUpdate = true;
      }
      if (!shouldUpdate) {
        return false;
      }

      res = true;
      const [statusRes, _serial, _ver] =
        await this.networkService.connectToScannerWifi(pipelineConfig.state.persistentValues);
      const filename = targetVersionFilename;
      const fileP = (this.http.get(`/assets/firmware/${filename}`, { responseType: 'blob' })).toPromise();
      const binFile = await fileP;
      const fd = new FormData();
      await new Promise((r) => setTimeout(r, 3000));
      fd.append('update', binFile, filename);
      const updateP = (this.http.post(SCANNER_APMODE_URL + '/updateUpload', fd,
        { responseType: 'text' }
      )).toPromise();
      await updateP;
      await new Promise((r) => setTimeout(r, 500));
    } catch (e: any) {
    } finally {
      if (res) {
        try {
          await new Promise((r) => setTimeout(r, 3000));

          try {
            await this.disconnectScanner(pipelineConfig.state.persistentValues, true);
          } catch (e) { console.log(e) }

        } catch (e2: any) {}
      }
    }
    return true;
  }

  async connectScanner(state: StateInterface): Promise<[boolean, string, number]> {
    const result = await this.networkService.connectToScannerWifi(state);
    if (!result[0] || state.DEMO_MODE || this.platform.is('mobileweb')) {
      return result;
    }

    try {
      await this.waitForScannerReady();
      return result;
    } catch (error) {
      this.logWifiStatusFailure('connect-ready', 0, Date.now(), error);
      return [false, '', 0];
    }
  }

  disconnectScanner(state: StateInterface, force = false) {
    return this.networkService.disconnectScanner(state, force);
  }
}
